import React, { useState, useCallback, useRef, forwardRef, useContext, useEffect } from 'react';
import { LassoAdorner, ResizeAdorner } from 'domains/designers/common';
import { useDragAndDrop, createDndEvent, IDndStartEvent as StartDndEvent } from 'hooks/useDragAndDrop';
import { useResize, createResizeEvent, IStartResizeEvent } from 'hooks/useResize';
import { DataDiagram } from './DataDiagram';
import { isLeftButtonPressed } from 'utils/KeyBoardUtils';
import { IResizeHandlerProps } from 'domains/designers/common/ResizeAdorner/ResizeHandler';
import {
  DiagramDisplayData,
  DiagramView,
  newDiagramView,
} from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/InputData';
import {
  getDataDiagramRect,
  getSelectedDiagrams,
  getDiagramsByIds,
  getSelectionDiagramsRect,
  hasChildProperties,
} from './DataDesignerUtils';
import classNames from 'classnames';
import { MultiSelectionAdorner } from 'domains/designers/common/MultiSelectionAdorner';
import { DataDescription } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { IRect, convertToRelativeCoordinates, isPointsEqual } from 'utils/GeometryUtils';
import { ResizeDirection } from '@decisions/server/DecisionsFramework/Design/Form';
import { mergeRefs } from 'utils/Refs';
import { intersect } from 'utils/ArrayUtils';
import { InputDataDndAdorner } from './DataDndAdorner';
import './DataDiagramView.less';
import { DataDesignerContext } from './DataDesignerContext';

export interface IDataDiagramViewProps {
  className?: string;
  diagrams: DiagramDisplayData[];
  selectedDiagramIds: string[];
  expandedDiagramIds: string[];
  onSelectionChanged: (diagramIds: string[]) => void;
  onDiagramViewUpdated: (diagramId: string, view: DiagramView) => void;
  getDataFields: (diagramId: string, path: string) => Promise<DataDescription[]>;
  onNameChanged: (diagramId: string, newName: string) => void;
  onDeleteDiagrams: (diagramIds: string[]) => void;
  onDiagramExpanded: (diagramId: string) => void;
  onDiagramCollapsed: (diagramId: string) => void;
}

/**
 * Represents Inputs/Outputs Data as a set of diagrams.
 * Every input/output has a corresponding diagram.
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export const DataDiagramView = forwardRef(
  (
    {
      diagrams,
      selectedDiagramIds,
      expandedDiagramIds,
      className,
      getDataFields,
      onNameChanged,
      onDiagramViewUpdated,
      onSelectionChanged,
      onDeleteDiagrams,
      onDiagramExpanded,
      onDiagramCollapsed,
    }: IDataDiagramViewProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const { noDiagramsMessage } = useContext(DataDesignerContext);

    const shiftDiagrams = useCallback(
      (diagrams: DiagramDisplayData[], shiftLeft: number, shiftTop: number) => {
        // we don't need to update position if it wasn't changed
        if (shiftLeft === 0 && shiftTop === 0) {
          return;
        }
        // TODO alex: replace with single API call
        diagrams.forEach(item => {
          const view = item.View ?? newDiagramView();
          view.Left = Math.max(view.Left + shiftLeft, 0);
          view.Top = Math.max(view.Top + shiftTop, 0);
          item.View = view;

          onDiagramViewUpdated(item.Id, item.View);
        });
      },
      [onDiagramViewUpdated]
    );

    const selectionLasso = useDragAndDrop({
      bounds: {
        left: 0,
        top: 0,
      },
      onDragging: result => {
        const selectedInputs = getSelectedDiagrams(diagrams, expandedDiagramIds, result.startPoint, result.endPoint);
        const selectedInputsIds = selectedInputs.map(input => input.Id);

        onSelectionChanged(selectedInputsIds);
      },
    });
    const diagramResize = useResize({
      bounds: {
        left: 0,
        top: 0,
      },
      onResizeEnd: result => {
        if (selectedDiagramIds != null && selectedDiagramIds.length === 1) {
          const resizedInput: DiagramDisplayData = diagrams.find(
            input => input.Id === selectedDiagramIds[0]
          ) as DiagramDisplayData;
          if (resizedInput) {
            const view = resizedInput.View || newDiagramView();
            view.Top = result.currentRect.top;
            view.Left = result.currentRect.left;
            view.Width = result.currentRect.width;

            const isExpanded = expandedDiagramIds.some(id => id === resizedInput.Id);
            // if diagram is collapsed we should not modify its height, because when diagram expands it should restore its height
            if (isExpanded) {
              view.Height = result.currentRect.height;
            }
            resizedInput.View = view;

            onDiagramViewUpdated(resizedInput.Id, resizedInput.View);
          }
        }
      },
    });
    const dndDiagram = useDragAndDrop({
      bounds: {
        left: 0,
        top: 0,
      },
      onDragEnd: result => {
        if (draggingDiagrams != null) {
          const draggedDiagram = intersect(diagrams, draggingDiagrams);
          const shiftLeft = result.endPoint.x - result.startPoint.x;
          const shiftTop = result.endPoint.y - result.startPoint.y;

          shiftDiagrams(draggedDiagram, shiftLeft, shiftTop);
          setDraggingDiagrams(null);
        }
      },
    });

    const rootRef = useRef<HTMLDivElement>(null);
    const [draggingDiagrams, setDraggingDiagrams] = useState<DiagramDisplayData[] | null>(null);

    const handlerAdornerMouseDown: IResizeHandlerProps['onMouseDown'] = useCallback(
      e => {
        if (selectedDiagramIds == null || selectedDiagramIds.length !== 1) {
          return;
        }

        const selectedDiagram = getDiagramsByIds(diagrams, selectedDiagramIds)[0];
        const isExpanded = expandedDiagramIds.some(id => id === selectedDiagram.Id);

        diagramResize.start(
          createResizeEvent<IStartResizeEvent>(e, {
            initialRect: getDataDiagramRect(selectedDiagram, isExpanded),
            direction: e.resizeDirection,
          })
        );
      },
      [diagramResize, expandedDiagramIds, diagrams, selectedDiagramIds]
    );

    const handleDiagramClick = useCallback(
      (diagram: DiagramDisplayData) => {
        onSelectionChanged([diagram.Id]);
      },
      [onSelectionChanged]
    );

    const handleDiagramMouseDown = useCallback(
      (e: React.MouseEvent<HTMLElement> & { diagram: DiagramDisplayData }) => {
        const selectedDiagrams = getDiagramsByIds(diagrams, selectedDiagramIds);
        const draggingDiagrams = selectedDiagrams.includes(e.diagram) ? selectedDiagrams : [e.diagram];
        setDraggingDiagrams(draggingDiagrams);

        const rect = getSelectionDiagramsRect(draggingDiagrams, expandedDiagramIds);

        dndDiagram.start(
          createDndEvent<StartDndEvent>(e, {
            initialPoint: { x: rect!.left, y: rect!.top },
          })
        );
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [diagrams, selectedDiagramIds]
    );

    const getResizeAdornerRect: () => IRect | null = useCallback(() => {
      const selectedDiagrams = getDiagramsByIds(diagrams, selectedDiagramIds);

      if (diagramResize.isActive) {
        return diagramResize.currentRect;
      } else if (selectedDiagrams == null || selectedDiagrams.length !== 1) {
        return null;
      } else {
        const isExpanded = expandedDiagramIds.some(id => id === selectedDiagrams[0].Id);
        return getDataDiagramRect(selectedDiagrams[0], isExpanded);
      }
    }, [diagrams, selectedDiagramIds, diagramResize.isActive, diagramResize.currentRect, expandedDiagramIds]);

    const handleViewMouseDown = useCallback(
      (e: React.MouseEvent<HTMLElement>) => {
        // make sure initially we clicked on diagram view and it do not bubbling from child elements
        // currentTarget vs target  https://medium.com/@etherealm/currenttarget-vs-target-in-js-2f3fd3a543e5
        if (e.currentTarget !== e.target || !isLeftButtonPressed(e)) {
          return;
        } else {
          if (selectedDiagramIds.length > 0) {
            onSelectionChanged([]);
          }

          if (rootRef.current) {
            selectionLasso.start(
              createDndEvent<StartDndEvent>(e, {
                initialPoint: convertToRelativeCoordinates(e, rootRef.current),
              })
            );
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [onSelectionChanged]
    );

    const preventAltEvent = useCallback(
      (event: KeyboardEvent) => {
        if (event.altKey && selectedDiagramIds?.length) {
          event.stopPropagation();
          event.preventDefault();
        }
      },
      [selectedDiagramIds?.length]
    );

    useEffect(() => {
      // prevent browser 'alt' + → and 'alt' + ← events when diagram is focused
      // to allow move diagram via keyboard when 'alt' key pressed
      window.addEventListener('keydown', preventAltEvent);
      return () => {
        window.removeEventListener('keydown', preventAltEvent);
      };
    }, [preventAltEvent]);

    const handleKeyUp = useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const selectedDiagrams = getDiagramsByIds(diagrams, selectedDiagramIds);
        const shift = e.ctrlKey ? 5 : 1;

        if (e.key.toLocaleLowerCase() === 'a' && e.ctrlKey) {
          onSelectionChanged(diagrams.map(item => item.Id));
        } else if (e.key === 'Delete') {
          onDeleteDiagrams(selectedDiagramIds);
        } else if (e.key === 'F2' && selectedDiagramIds.length === 1) {
          //TODO alex: need to show inline editor for renaming diagram
        } else if (e.altKey && e.key === 'ArrowLeft') {
          shiftDiagrams(selectedDiagrams, -1 * shift, 0);
        } else if (e.altKey && e.key === 'ArrowRight') {
          shiftDiagrams(selectedDiagrams, shift, 0);
        } else if (e.altKey && e.key === 'ArrowUp') {
          shiftDiagrams(selectedDiagrams, 0, -1 * shift);
        } else if (e.altKey && e.key === 'ArrowDown') {
          shiftDiagrams(selectedDiagrams, 0, shift);
        } else if (e.altKey && e.key === 'Escape') {
          onSelectionChanged([]);
        }
      },
      [diagrams, onDeleteDiagrams, onSelectionChanged, selectedDiagramIds, shiftDiagrams]
    );

    const handleDiagramExpanded = useCallback(
      (diagramId: string, isExpanded: boolean) => {
        if (isExpanded) {
          onDiagramExpanded(diagramId);
        } else {
          onDiagramCollapsed(diagramId);
        }
      },
      [onDiagramCollapsed, onDiagramExpanded]
    );

    return (
      <div
        className={classNames('dp-data-diagram-view', 'full-size', className)}
        onMouseDown={handleViewMouseDown}
        onKeyUp={handleKeyUp}
        // onKeyUp event will not fire without "tabIndex", "-1" value guarantees that it will not allow focus via keyboard
        tabIndex={-1}
        ref={mergeRefs([rootRef, ref])}
      >
        {((selectionLasso.isActive && selectedDiagramIds) ||
          (!selectionLasso.isActive && selectedDiagramIds && selectedDiagramIds.length > 1)) && (
          <MultiSelectionAdorner
            showMutualRect={!selectionLasso.isActive}
            innerRecs={getDiagramsByIds(diagrams, selectedDiagramIds).map(diagram => {
              const isExpanded = expandedDiagramIds.some(id => id === diagram.Id);
              return getDataDiagramRect(diagram, isExpanded);
            })}
            margin={8}
          />
        )}
        {selectedDiagramIds.length === 1 && (
          <ResizeAdorner
            zIndex={999}
            margin={8}
            resizeDirections={
              !hasChildProperties(getDiagramsByIds(diagrams, selectedDiagramIds)[0]) ||
              !expandedDiagramIds.some(id => id === selectedDiagramIds[0])
                ? [ResizeDirection.Left, ResizeDirection.Right]
                : [ResizeDirection.All]
            }
            handlerMouseDown={handlerAdornerMouseDown}
            {...(getResizeAdornerRect() as IRect)}
          />
        )}
        {selectionLasso.isActive && (
          <LassoAdorner zIndex={999} startPoint={selectionLasso.startPoint} endPoint={selectionLasso.endPoint} />
        )}
        {/* We need to show  DndAdorner only when startPoint != endPoint, otherwise it will be shown when click on diagram*/}
        {draggingDiagrams && !isPointsEqual(dndDiagram.startPoint, dndDiagram.endPoint) && (
          <InputDataDndAdorner
            left={dndDiagram.endPoint.x}
            top={dndDiagram.endPoint.y}
            diagrams={draggingDiagrams}
            expandedInputsIds={expandedDiagramIds}
          />
        )}
        {diagrams.map(input => (
          <DataDiagram
            key={input.Id}
            diagramData={input}
            onMouseDown={handleDiagramMouseDown}
            onClick={handleDiagramClick}
            getDataFields={getDataFields}
            onNameChanged={onNameChanged}
            onRootNodeExpand={handleDiagramExpanded}
          />
        ))}
        {diagrams == null ||
          (diagrams.length === 0 && (
            <div className="dp-data-diagram-view__prompt full-size flex-centering">{noDiagramsMessage}</div>
          ))}
      </div>
    );
  }
);
